import { useCallback, useMemo, useState } from 'react';

export const useManagedPromise = <T>() => {
    const [promise, setPromise] = useState<Promise<T>>();
    const [promiseResolve, setPromiseResolve] = useState<(value: T | PromiseLike<T>) => void>();
    const [promiseReject, setPromiseReject] = useState<(reason?: string) => void>();

    /**
     * Resolves promise if one is stored, then clears it. Does nothing if no promise is stored.
     */
    const resolve = useCallback(
        (value: T | PromiseLike<T>) => {
            if (promiseResolve) {
                promiseResolve(value);
            }

            setPromise(undefined);
            setPromiseResolve(undefined);
            setPromiseReject(undefined);
        },
        [promiseResolve],
    );

    /**
     * Rejects promise if one is stored, then clears it. Does nothing if no promise is stored.
     */
    const reject = useCallback(
        (reason?: string) => {
            if (promiseReject) {
                promiseReject(reason);
            }

            setPromise(undefined);
            setPromiseResolve(undefined);
            setPromiseReject(undefined);
        },
        [promiseReject],
    );

    /**
     * Creates promise, stores it, and it's resolve/reject callbacks, and returns it.
     */
    const create = useCallback(() => {
        if (promiseReject) {
            promiseReject();
        }

        const newPromise = new Promise<T>((res, rej) => {
            setPromiseResolve(() => res);
            setPromiseReject(() => rej);
        });
        setPromise(newPromise);

        return newPromise;
    }, [promiseReject]);

    return useMemo(
        () => ({
            value: promise,
            create,
            resolve,
            reject,
        }),
        [promise, create, resolve, reject],
    );
};
